/**
 * CommandMenu - @ Command Flyout
 * 
 * Shows available @ commands when user types @ in the chat input.
 * Allows selection via click or continued typing to filter.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui';

// Command definitions with icons and descriptions
const COMMANDS = [
  { 
    command: 'open', 
    icon: '📂', 
    label: 'Open Document', 
    description: 'Open a document in a new tab',
    shortcut: '@open',
  },
  { 
    command: 'edit', 
    icon: '✏️', 
    label: 'Edit Document', 
    description: 'Open a document in edit mode',
    shortcut: '@edit',
  },
  { 
    command: 'close', 
    icon: '✕', 
    label: 'Close Tab', 
    description: 'Close an open document tab',
    shortcut: '@close',
  },
  { 
    command: 'plan', 
    icon: '📋', 
    label: 'Plan Changes', 
    description: 'Create a plan for document changes',
    shortcut: '@plan',
  },
  { 
    command: 'ref', 
    icon: '📚', 
    label: 'Reference Library', 
    description: 'Browse reference documents',
    shortcut: '@ref',
  },
  { 
    command: 'context', 
    icon: '📎', 
    label: 'Add Context', 
    description: 'Add a document to chat context',
    shortcut: '@context',
  },
  { 
    command: 'project', 
    icon: '🗂️', 
    label: 'Switch Project', 
    description: 'Switch to a different project',
    shortcut: '@project',
  },
  { 
    command: 'go', 
    icon: '🚀', 
    label: 'Navigate', 
    description: 'Go to a page in the app',
    shortcut: '@go',
  },
  { 
    command: 'block', 
    icon: '§', 
    label: 'Block Reference', 
    description: 'Reference a specific block by ID',
    shortcut: '@block',
  },
];

interface CommandMenuProps {
  isOpen: boolean;
  query: string;
  onSelect: (command: string) => void;
  onClose: () => void;
}

export function CommandMenu({ isOpen, query, onSelect, onClose }: CommandMenuProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Filter commands based on query
  const filteredCommands = useMemo(() => {
    if (!query) return COMMANDS;
    const lowerQuery = query.toLowerCase();
    return COMMANDS.filter(cmd => 
      cmd.command.toLowerCase().includes(lowerQuery) ||
      cmd.label.toLowerCase().includes(lowerQuery)
    );
  }, [query]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredCommands.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredCommands.length - 1
        );
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          onSelect(filteredCommands[selectedIndex].command);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredCommands, onSelect, onClose]);

  if (!isOpen) return null;

  return (
    <div className="absolute bottom-full left-0 mb-2 w-80 max-h-96 overflow-y-auto bg-card border border-border rounded-lg shadow-xl z-50">
      {/* Header */}
      <div className="sticky top-0 bg-card px-3 py-2 border-b border-border">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">
            @ Commands
          </span>
          <span className="text-xs text-muted-foreground">
            ↑↓ navigate • ↵ select • esc close
          </span>
        </div>
      </div>

      {/* Command list */}
      <div className="py-1">
        {filteredCommands.length === 0 ? (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            No matching commands
          </div>
        ) : (
          filteredCommands.map((cmd, index) => (
            <Button
              key={cmd.command}
              onClick={() => onSelect(cmd.command)}
              onMouseEnter={() => setSelectedIndex(index)}
              variant="ghost"
              className={`w-full flex items-start gap-3 px-3 py-2 h-auto justify-start rounded-none ${
                index === selectedIndex
                  ? 'bg-info-light'
                  : ''
              }`}
            >
              <span className="text-lg flex-shrink-0 w-6 text-center">
                {cmd.icon}
              </span>
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-2">
                  <span className={`font-medium text-sm ${
                    index === selectedIndex
                      ? 'text-info'
                      : 'text-foreground'
                  }`}>
                    {cmd.label}
                  </span>
                  <code className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                    {cmd.shortcut}
                  </code>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {cmd.description}
                </p>
              </div>
            </Button>
          ))
        )}
      </div>

      {/* Footer hint */}
      <div className="sticky bottom-0 bg-muted px-3 py-1.5 border-t border-border">
        <span className="text-xs text-muted-foreground">
          Type to filter, or keep typing the command name
        </span>
      </div>
    </div>
  );
}

export default CommandMenu;




